import { osnovaApi } from '../base';
import type { EconAutoCollectPreview, EconCashCalendarWeek, EconChatMessage, EconChatSession, EconCostItemLine, EconDictName, EconExecPackResponse, EconFactTrace, EconInfo, EconObjectEconomy, EconPlanLine, EconPlanVersion, EconProfitSplitPeriod, EconRun, EconRunDetail, EconTemplate, NewEconPlanLine } from '../types';

export const econApi = osnovaApi.injectEndpoints({
  endpoints: (b) => ({
    /** Карточка агента + три модуля. */
    getEconInfo: b.query<EconInfo, void>({
      query: () => '/agents/econ/info',
    }),

    /** Список прогонов отдела с автором (опц. фильтр по модулю; `mine` — только
     *  свои). Статус прогона меняется асинхронно — страница включает polling
     *  через `{ pollingInterval }` пока есть pending/processing (как VolumeChecks). */
    listEconRuns: b.query<
      { items: EconRun[] },
      { module?: string; mine?: boolean; limit?: number; offset?: number } | void
    >({
      query: (p) => ({ url: '/agents/econ/runs', params: p ?? undefined }),
      providesTags: ['EconRun'],
    }),

    /** Карточка прогона: прогон + источники + строки + факты + аномалии. */
    getEconRun: b.query<EconRunDetail, string>({
      query: (id) => `/agents/econ/runs/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'EconRun', id }],
    }),

    /** Платёжный календарь прогона М4 (снимки по неделям горизонта). */
    getEconCashCalendar: b.query<{ weeks: EconCashCalendarWeek[] }, string>({
      query: (id) => `/agents/econ/runs/${id}/cash-calendar`,
      providesTags: (_r, _e, id) => [{ type: 'EconRun', id }],
    }),

    /** Пакет руководству М5: KPI по направлениям + статус согласованности (Ц6)
     *  + LLM-нарратив. `pack: null`, пока прогон exec_pack не собран воркером.
     *  Инвалидируется тем же тегом `EconRun`, что и карточка прогона (WS
     *  `econ_run_updated` перетянет пакет по завершении сборки). */
    getEconExecPack: b.query<EconExecPackResponse, string>({
      query: (id) => `/agents/econ/runs/${id}/exec-pack`,
      providesTags: (_r, _e, id) => [{ type: 'EconRun', id }],
    }),

    /** Создать прогон: multipart `module`, `period` (YYYY-MM-DD | YYYY-MM),
     *  опц. `object_scope` и файлы (имя поля файла несёт doc_type). */
    createEconRun: b.mutation<{ run_id: string; status: string }, FormData>({
      query: (form) => ({ url: '/agents/econ/runs', method: 'POST', body: form }),
      invalidatesTags: ['EconRun'],
    }),

    deleteEconRun: b.mutation<{ ok: boolean }, string>({
      query: (id) => ({ url: `/agents/econ/runs/${id}`, method: 'DELETE' }),
      invalidatesTags: ['EconRun'],
    }),

    /** HITL: решение экономиста по прогону (accepted | rejected | disputed).
     *  При `accepted` backend ставит сборку отчёта (`report_queued: true`). */
    decideEconRun: b.mutation<
      { ok: boolean; report_queued: boolean },
      { id: string; decision: string; comment?: string }
    >({
      query: ({ id, decision, comment }) => ({
        url: `/agents/econ/runs/${id}/decision`,
        method: 'PATCH',
        body: { decision, comment },
      }),
      invalidatesTags: (_r, _e, { id }) => [{ type: 'EconRun', id }, 'EconRun'],
    }),

    /** HITL: решение экономиста по аномалии. `runId` передаём из карточки —
     *  backend не возвращает run_id, а инвалидировать нужно именно прогон. */
    annotateEconAnomaly: b.mutation<
      { ok: boolean },
      { id: string; runId: string; decision: string; comment?: string }
    >({
      query: ({ id, decision, comment }) => ({
        url: `/agents/econ/anomalies/${id}`,
        method: 'PATCH',
        body: { decision, comment },
      }),
      invalidatesTags: (_r, _e, { runId }) => [
        { type: 'EconRun', id: runId },
        { type: 'EconAnomaly', id: runId },
      ],
    }),

    /** HITL: ручная коррекция извлечённой строки (полная замена payload,
     *  снимает needs_review). `runId` — для инвалидации карточки прогона. */
    patchEconRow: b.mutation<
      { ok: boolean },
      { id: string; runId: string; payload: Record<string, unknown> }
    >({
      query: ({ id, payload }) => ({
        url: `/agents/econ/extracted-rows/${id}`,
        method: 'PATCH',
        body: { payload },
      }),
      invalidatesTags: (_r, _e, { runId }) => [{ type: 'EconRun', id: runId }],
    }),

    /** Трассировка факта по record_uid: факт → исходные строки → источники. */
    traceEconFact: b.query<EconFactTrace, string>({
      query: (recordUid) => `/agents/econ/facts/${recordUid}/trace`,
    }),

    /** Выгрузка итогового XLSX-отчёта прогона как Blob. Скачивание под
     *  RBAC — Authorization-заголовок ставит baseQuery. Имя файла backend
     *  формирует ASCII-safe (`econ_report_<id>.xlsx`), страница знает id, так
     *  что потеря Content-Disposition в RTK Query не критична. `no-store` —
     *  отчёт мог быть перегенерирован, не отдаём из HTTP-кеша. Используется
     *  лениво (`useLazyExportEconXlsxQuery`): запрос по клику «Скачать». */
    exportEconXlsx: b.query<Blob, string>({
      query: (id) => ({
        url: `/agents/econ/runs/${id}/export-xlsx`,
        cache: 'no-store',
        responseHandler: (response) => response.blob(),
      }),
    }),

    // ---- XLSX-шаблоны отчётов (управление версиями) ----

    /** Список версий шаблонов (опц. фильтр по модулю). Без blob. */
    listEconTemplates: b.query<{ items: EconTemplate[] }, { module?: string } | void>({
      query: (p) => ({ url: '/agents/econ/templates', params: p ?? undefined }),
      providesTags: ['EconTemplate'],
    }),

    /** Залить новую версию шаблона (write). multipart: `module`, `file`,
     *  опц. `sheet_contract` (JSON-строка) и `activate` ("true"). Версия —
     *  max+1 по модулю на backend. Возвращает краткую карточку версии.
     *
     *  Для модуля `bdr` без ручного `sheet_contract` backend строит карту
     *  привязки АВТОМАТИЧЕСКИ из самого шаблона: `auto_mapped: true`,
     *  `contracts` — привязанные договоры, `unmatched` — без строки-итога в
     *  реестрах (предупреждение). Для ручного override / mining / canteen —
     *  `auto_mapped: false` и пустые списки. */
    uploadEconTemplate: b.mutation<
      {
        id: string;
        module: string;
        version: number;
        is_active: boolean;
        auto_mapped: boolean;
        matched_count: number;
        unmatched_count: number;
        contracts: string[];
        unmatched: string[];
      },
      FormData
    >({
      query: (form) => ({ url: '/agents/econ/templates', method: 'POST', body: form }),
      invalidatesTags: ['EconTemplate'],
    }),

    /** Сделать версию шаблона активной (write). Снимает активность с прочих
     *  версий того же модуля (на backend в транзакции). */
    activateEconTemplate: b.mutation<{ ok: boolean }, string>({
      query: (id) => ({ url: `/agents/econ/templates/${id}/activate`, method: 'POST' }),
      invalidatesTags: ['EconTemplate'],
    }),

    /** Скачать XLSX-шаблон версии как Blob (read). Имя файла backend отдаёт в
     *  Content-Disposition, но оно теряется в RTK Query — имя формируем на
     *  клиенте из метаданных версии. `no-store` — версия могла быть перезалита. */
    downloadEconTemplate: b.query<Blob, string>({
      query: (id) => ({
        url: `/agents/econ/templates/${id}/download`,
        cache: 'no-store',
        responseHandler: (response) => response.blob(),
      }),
    }),

    // ---- Чат-расследование по прогону (read-only) ----

    /** Сессии чата по прогону (новые сверху). */
    listEconChatSessions: b.query<{ items: EconChatSession[] }, string>({
      query: (runId) => `/agents/econ/runs/${runId}/chat/sessions`,
      providesTags: (_r, _e, runId) => [{ type: 'EconChatSession', id: runId }],
    }),

    /** Сообщения сессии в хронологическом порядке. */
    listEconChatMessages: b.query<{ items: EconChatMessage[] }, string>({
      query: (sessionId) => `/agents/econ/chat/sessions/${sessionId}/messages`,
      providesTags: (_r, _e, sessionId) => [{ type: 'EconChatSession', id: sessionId }],
    }),

    /** Задать вопрос по прогону. При отсутствии `session_id` backend создаёт
     *  новую сессию и возвращает её id + сохранённое сообщение ассистента.
     *  Инвалидируем сессии прогона и сообщения сессии (после ответа известен
     *  её id), чтобы тред перечитался. */
    askEconChat: b.mutation<
      { session_id: string; message: EconChatMessage },
      { runId: string; question: string; session_id?: string }
    >({
      query: ({ runId, question, session_id }) => ({
        url: `/agents/econ/runs/${runId}/chat`,
        method: 'POST',
        body: { question, session_id },
      }),
      invalidatesTags: (res, _e, { runId }) => {
        const tags: Array<{ type: 'EconChatSession'; id: string }> = [
          { type: 'EconChatSession', id: runId },
        ];
        if (res) tags.push({ type: 'EconChatSession', id: res.session_id });
        return tags;
      },
    }),

    // ---- Справочники econ (ТЗ §7.3): master-data CRUD ----
    // Универсальные эндпоинты по dict ∈ objects|cost_articles|cashflow_articles|
    // licenses|profit_split. Тело (body) — соответствующий New*-DTO; ответ
    // содержит dict и item. Тип item уточняется на стороне компонента.

    /** Список записей справочника (опц. ?active=true, по умолчанию true). */
    listEconDict: b.query<
      { dict: EconDictName; items: unknown[] },
      { dict: EconDictName; active?: boolean }
    >({
      query: ({ dict, active }) => ({
        url: `/agents/econ/handbooks/${dict}`,
        params: active === undefined ? undefined : { active },
      }),
      providesTags: (_r, _e, { dict }) => [{ type: 'EconDictionary', id: dict }],
    }),

    /** Создать запись (profit_split — новая версия, тело {object_id, period_month, weight}). */
    createEconDict: b.mutation<
      { dict: EconDictName; item: unknown },
      { dict: EconDictName; body: Record<string, unknown> }
    >({
      query: ({ dict, body }) => ({
        url: `/agents/econ/handbooks/${dict}`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_r, _e, { dict }) => [{ type: 'EconDictionary', id: dict }],
    }),

    /** Обновить запись (profit_split — через POST новой версии). */
    updateEconDict: b.mutation<
      { dict: EconDictName; item: unknown },
      { dict: EconDictName; id: string; body: Record<string, unknown> }
    >({
      query: ({ dict, id, body }) => ({
        url: `/agents/econ/handbooks/${dict}/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_r, _e, { dict }) => [{ type: 'EconDictionary', id: dict }],
    }),

    /** Активировать запись. */
    activateEconDict: b.mutation<{ ok: boolean }, { dict: EconDictName; id: string }>({
      query: ({ dict, id }) => ({
        url: `/agents/econ/handbooks/${dict}/${id}/activate`,
        method: 'POST',
      }),
      invalidatesTags: (_r, _e, { dict }) => [{ type: 'EconDictionary', id: dict }],
    }),

    /** Деактивировать (soft-delete: is_active=false). */
    deactivateEconDict: b.mutation<{ ok: boolean }, { dict: EconDictName; id: string }>({
      query: ({ dict, id }) => ({
        url: `/agents/econ/handbooks/${dict}/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_r, _e, { dict }) => [{ type: 'EconDictionary', id: dict }],
    }),

    // Версии плановых данных БДР (Р-14, ТЗ §4.8). Активная версия — база
    // план/факт новых прогонов; утверждённые прогоны не пересчитываются.
    listEconPlanVersions: b.query<
      { module: string; versions: EconPlanVersion[] },
      { module?: string } | void
    >({
      query: (p) => ({
        url: '/agents/econ/plan-versions',
        params: p?.module ? { module: p.module } : undefined,
      }),
      providesTags: ['EconPlanVersion'],
    }),
    getEconPlanVersion: b.query<
      { version: EconPlanVersion | null; lines: EconPlanLine[] },
      string
    >({
      query: (id) => `/agents/econ/plan-versions/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'EconPlanVersion', id }],
    }),
    createEconPlanVersion: b.mutation<
      { version: EconPlanVersion },
      { module?: string; label: string; lines: NewEconPlanLine[] }
    >({
      query: (body) => ({ url: '/agents/econ/plan-versions', method: 'POST', body }),
      invalidatesTags: ['EconPlanVersion'],
    }),
    activateEconPlanVersion: b.mutation<{ version: EconPlanVersion }, string>({
      query: (id) => ({ url: `/agents/econ/plan-versions/${id}/activate`, method: 'POST' }),
      invalidatesTags: ['EconPlanVersion'],
    }),
    deleteEconPlanVersion: b.mutation<{ deleted: string }, string>({
      query: (id) => ({ url: `/agents/econ/plan-versions/${id}`, method: 'DELETE' }),
      invalidatesTags: ['EconPlanVersion'],
    }),

    // ---- Автосбор фактов из смежных модулей и карточка финансового здоровья объекта ----
    previewEconAutoCollect: b.query<
      EconAutoCollectPreview,
      { period: string; object_scope?: string }
    >({
      query: (params) => ({
        url: '/agents/econ/auto-collect-preview',
        params,
      }),
      providesTags: ['EconRun'],
    }),
    autoCollectEconRun: b.mutation<
      { run_id: string; status: string },
      {
        module?: string;
        period: string;
        object_scope?: string;
        include_ks?: boolean;
        include_field?: boolean;
        include_adjacent?: boolean;
      }
    >({
      query: (body) => ({
        url: '/agents/econ/auto-collect',
        method: 'POST',
        body: { module: 'bdr', ...body },
      }),
      invalidatesTags: ['EconRun'],
    }),
    getEconObjectEconomy: b.query<
      EconObjectEconomy,
      { project_id?: string; object_name?: string }
    >({
      query: (params) => ({
        url: '/agents/econ/object-economy',
        params,
      }),
      providesTags: ['EconRun'],
    }),

    /** Активные доли распределения прибыли периода (`YYYY-MM`). */
    getEconProfitSplit: b.query<EconProfitSplitPeriod, string>({
      query: (period) => `/agents/econ/profit-split/${period}`,
      providesTags: (_r, _e, period) => [{ type: 'EconDictionary', id: `profit_split:${period}` }],
    }),

    /** Заменить распределение прибыли периода целиком (одна транзакция, Σ = 100 %). */
    replaceEconProfitSplit: b.mutation<
      EconProfitSplitPeriod,
      { period: string; items: { object_id: string; weight: number }[] }
    >({
      query: ({ period, items }) => ({
        url: `/agents/econ/profit-split/${period}`,
        method: 'PUT',
        body: { items },
      }),
      invalidatesTags: (_r, _e, { period }) => [
        { type: 'EconDictionary', id: `profit_split:${period}` },
        { type: 'EconDictionary', id: 'profit_split' },
      ],
    }),

    /** Импортировать единичные расценки в справочник ПЭО из ЛСР и смет Сметчика. */
    importEconUnitRatesFromSmeta: b.mutation<
      { imported_count: number },
      { contract_id?: string } | void
    >({
      query: (body) => ({
        url: '/agents/econ/handbooks/unit_rates/import-from-smeta',
        method: 'POST',
        body: body ?? {},
      }),
      invalidatesTags: [{ type: 'EconDictionary', id: 'unit_rates' }],
    }),

    /** Справочник единичных расценок книгой XLSX — те же фильтры, что у списка. */
    exportEconUnitRatesXlsx: b.query<Blob, { active?: boolean; contract_text?: string } | void>({
      query: (p) => ({
        url: '/agents/econ/handbooks/unit_rates/export.xlsx',
        params: p ? { active: p.active, contract_text: p.contract_text } : undefined,
        cache: 'no-store',
        responseHandler: (response) => response.blob(),
      }),
    }),

    /** Строки расходов БДР v2 для статей затрат 1С — по группам листа «Статьи в БДР». */
    listEconCostItemLines: b.query<{ items: EconCostItemLine[] }, void>({
      query: () => '/agents/econ/handbooks/cost_items_1c/lines',
      providesTags: [{ type: 'EconDictionary', id: 'cost_items_1c:lines' }],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetEconInfoQuery,
  useListEconRunsQuery,
  useGetEconRunQuery,
  useGetEconCashCalendarQuery,
  useGetEconExecPackQuery,
  useCreateEconRunMutation,
  useDeleteEconRunMutation,
  useDecideEconRunMutation,
  useAnnotateEconAnomalyMutation,
  usePatchEconRowMutation,
  useTraceEconFactQuery,
  useLazyTraceEconFactQuery,
  useLazyExportEconXlsxQuery,
  useListEconTemplatesQuery,
  useUploadEconTemplateMutation,
  useActivateEconTemplateMutation,
  useLazyDownloadEconTemplateQuery,
  useListEconChatSessionsQuery,
  useListEconChatMessagesQuery,
  useAskEconChatMutation,
  useListEconDictQuery,
  useCreateEconDictMutation,
  useUpdateEconDictMutation,
  useActivateEconDictMutation,
  useDeactivateEconDictMutation,
  useListEconPlanVersionsQuery,
  useGetEconPlanVersionQuery,
  useCreateEconPlanVersionMutation,
  useActivateEconPlanVersionMutation,
  useDeleteEconPlanVersionMutation,
  usePreviewEconAutoCollectQuery,
  useAutoCollectEconRunMutation,
  useGetEconObjectEconomyQuery,
  useGetEconProfitSplitQuery,
  useReplaceEconProfitSplitMutation,
  useImportEconUnitRatesFromSmetaMutation,
  useLazyExportEconUnitRatesXlsxQuery,
  useListEconCostItemLinesQuery,
} = econApi;
